import { useState, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { mapRows } from '@/lib/db-utils'

export type GoverningBody = 'NRA' | 'CPSA' | 'NSRA' | 'UKPSA' | 'MLAGB' | 'ALL'

export type IdType =
  | 'nra_number'
  | 'tr_number'
  | 'grp_number'
  | 'cpsa_number'
  | 'nsra_number'
  | 'ukpsa_number'
  | 'mlagb_number'
  | 'all'

export interface IdLookupResult {
  id: string
  name: string
  governingBody: GoverningBody
  idNumber: string
  idType: string
  classification: string | null
  isOnPlatform: boolean
  profileId: string | null
}

export interface IdLookupFilters {
  governingBody: GoverningBody
  idType: IdType
  idNumber: string
  firstName: string
  lastName: string
}

/** Map governing body to the ID type options available */
export function getIdTypesForBody(body: GoverningBody): { value: IdType; label: string }[] {
  switch (body) {
    case 'NRA':
      return [
        { value: 'nra_number', label: 'NRA Number' },
        { value: 'tr_number', label: 'TR Number' },
        { value: 'grp_number', label: 'GR&P Number' },
      ]
    case 'CPSA':
      return [{ value: 'cpsa_number', label: 'CPSA Number' }]
    case 'NSRA':
      return [{ value: 'nsra_number', label: 'NSRA Number' }]
    case 'UKPSA':
      return [{ value: 'ukpsa_number', label: 'UKPSA Number' }]
    case 'MLAGB':
      return [{ value: 'mlagb_number', label: 'MLAGB Number' }]
    case 'ALL':
      return [{ value: 'all', label: 'All ID Types' }]
    default:
      return [{ value: 'all', label: 'All ID Types' }]
  }
}

/** Auto-detect the governing body from an ID number format */
export function autoDetectGoverningBody(idNumber: string): GoverningBody | null {
  const trimmed = idNumber.trim().toUpperCase()

  // NRA numbers are typically 4-6 digit numeric
  if (/^\d{4,6}$/.test(trimmed)) return null // Could be any — ambiguous

  // CPSA numbers: usually format EE12345 (2 letters + 5 digits)
  if (/^[A-Z]{2}\d{5}$/.test(trimmed)) return 'CPSA'

  // NSRA numbers: typically N followed by digits
  if (/^N\d{4,6}$/.test(trimmed)) return 'NSRA'

  // UKPSA numbers: typically UK followed by digits
  if (/^UK\d{4,6}$/.test(trimmed)) return 'UKPSA'

  // MLAGB: typically ML followed by digits
  if (/^ML\d{4,6}$/.test(trimmed)) return 'MLAGB'

  return null
}

interface UseIdLookupReturn {
  results: IdLookupResult[]
  loading: boolean
  error: string | null
  search: (filters: IdLookupFilters) => Promise<void>
  clearResults: () => void
}

export function useIdLookup(): UseIdLookupReturn {
  const [results, setResults] = useState<IdLookupResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const search = useCallback(async (filters: IdLookupFilters) => {
    setLoading(true)
    setError(null)
    setResults([])

    try {
      let query = supabase.from('profiles').select('*')

      // Build search conditions based on filters
      if (filters.idNumber.trim()) {
        const idNum = filters.idNumber.trim()

        if (filters.governingBody === 'ALL' || filters.idType === 'all') {
          // Search across all ID fields using or()
          query = query.or(
            `nra_membership_number.eq.${idNum},grid_number.eq.${idNum},trid_number.eq.${idNum},cpsa_number.eq.${idNum},nsra_number.eq.${idNum},ukpsa_number.eq.${idNum},mlagb_number.eq.${idNum}`,
          )
        } else {
          // Map IdType to the database column
          const columnMap: Record<string, string> = {
            nra_number: 'nra_membership_number',
            tr_number: 'trid_number',
            grp_number: 'grid_number',
            cpsa_number: 'cpsa_number',
            nsra_number: 'nsra_number',
            ukpsa_number: 'ukpsa_number',
            mlagb_number: 'mlagb_number',
          }
          const col = columnMap[filters.idType]
          if (col) {
            query = query.eq(col, idNum)
          }
        }
      }

      // Name-based search
      if (filters.firstName.trim() || filters.lastName.trim()) {
        const nameParts: string[] = []
        if (filters.firstName.trim()) {
          nameParts.push(filters.firstName.trim())
        }
        if (filters.lastName.trim()) {
          nameParts.push(filters.lastName.trim())
        }
        // Use ilike for partial name matching
        if (nameParts.length > 0) {
          query = query.ilike('name', `%${nameParts.join('%')}%`)
        }
      }

      query = query.limit(20)

      const { data, error: err } = await query

      if (!isMounted.current) return

      if (err) {
        setError(err.message)
        setLoading(false)
        return
      }

      const profiles = mapRows<{
        id: string
        name: string
        nraMembershipNumber: string | null
        gridNumber: string | null
        tridNumber: string | null
        cpsaNumber: string | null
        nsraNumber: string | null
        ukpsaNumber: string | null
        mlagbNumber: string | null
      }>(data as Record<string, unknown>[] | null)

      // Build result entries — one per matching governing body ID
      const lookupResults: IdLookupResult[] = []

      for (const p of profiles) {
        const idEntries: { body: GoverningBody; number: string; type: string }[] = []

        if (p.nraMembershipNumber) {
          idEntries.push({ body: 'NRA', number: p.nraMembershipNumber, type: 'NRA Number' })
        }
        if (p.gridNumber) {
          idEntries.push({ body: 'NRA', number: p.gridNumber, type: 'GR&P Number' })
        }
        if (p.tridNumber) {
          idEntries.push({ body: 'NRA', number: p.tridNumber, type: 'TR Number' })
        }
        if (p.cpsaNumber) {
          idEntries.push({ body: 'CPSA', number: p.cpsaNumber, type: 'CPSA Number' })
        }
        if (p.nsraNumber) {
          idEntries.push({ body: 'NSRA', number: p.nsraNumber, type: 'NSRA Number' })
        }
        if (p.ukpsaNumber) {
          idEntries.push({ body: 'UKPSA', number: p.ukpsaNumber, type: 'UKPSA Number' })
        }
        if (p.mlagbNumber) {
          idEntries.push({ body: 'MLAGB', number: p.mlagbNumber, type: 'MLAGB Number' })
        }

        // Filter by governing body if specified
        const filteredEntries =
          filters.governingBody === 'ALL'
            ? idEntries
            : idEntries.filter((e) => e.body === filters.governingBody)

        // If searching by ID number and no entries match the filter, add the full match
        if (filteredEntries.length === 0 && !filters.idNumber.trim()) continue

        if (filteredEntries.length > 0) {
          for (const entry of filteredEntries) {
            lookupResults.push({
              id: `${p.id}-${entry.type}`,
              name: p.name || 'Unknown',
              governingBody: entry.body,
              idNumber: entry.number,
              idType: entry.type,
              classification: null, // Classification would come from a separate table
              isOnPlatform: true,
              profileId: p.id,
            })
          }
        } else {
          // Name search match without specific ID numbers for the selected body
          lookupResults.push({
            id: p.id,
            name: p.name || 'Unknown',
            governingBody: filters.governingBody === 'ALL' ? 'NRA' : filters.governingBody,
            idNumber: '-',
            idType: 'N/A',
            classification: null,
            isOnPlatform: true,
            profileId: p.id,
          })
        }
      }

      setResults(lookupResults)
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'An error occurred while searching')
      }
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }, [])

  const clearResults = useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  return { results, loading, error, search, clearResults }
}
